import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import ResourcePresenceManager from 'nav/presence/ResourcePresenceManager';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import DocumentStore from 'notebook/documents/DocumentStore';
import useDocumentSaveState from 'notebook/documents/useDocumentSaveState';
import {LoadedDocument, TaskData} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import WorksheetStepHeader from 'notebook/worksheets/workspace/WorksheetStepHeader';
import getTestProps from 'test/lib/cypress/getTestProps';

import BannerMessage from '../util/BannerMessage';

export interface WorksheetStepFrameProps {
  stepName?: string;
  document: LoadedDocument;
  stepTaskData: TaskData;
  tableNamespace?: string;
  enableStepTransitions?: boolean;
  isWorksheetStartRequired: boolean;
  isSelectedStepActionsLocked: boolean;
  isSelectedStepDocumentLocked: boolean;
  isExecutionEnabled: boolean;
  worksheetReviewStatus?: ReviewStatus | null;
  isWorksheetArchived?: boolean;
}

function WorksheetStepFrame(props: WorksheetStepFrameProps): JSX.Element | null {
  const {
    stepName,
    document,
    stepTaskData,
    tableNamespace,
    enableStepTransitions,
    isSelectedStepActionsLocked,
    isSelectedStepDocumentLocked,
    isExecutionEnabled,
    isWorksheetArchived,
  } = props;
  const {refetch, worksheet} = WorksheetContext.useContext();

  const {isLatest, latestVersionId} = useDocumentSaveState(document.id);
  const bannerProps = {...props, isLatest, latestVersionId};

  const onDocumentChange = useCallback(
    (wasServerUpdate: boolean, changedDocumentId: string) => {
      if (wasServerUpdate) {
        refetch(wasServerUpdate);
      }
    },
    [refetch]
  );

  const relatedDocumentIds = useMemo(
    () =>
      BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
        ? (worksheet?.worksheetStepGroups.flatMap((group) =>
            group.worksheetSteps.map((step) => step.document.id)
          ) ?? [])
        : undefined,
    [worksheet?.worksheetStepGroups]
  );

  useEffect(() => {
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      assert(
        DocumentStore.getDocument(document.id) != null,
        'Cross-step data refs implicitly requires the document to be initialized during fetchAllDocumentsInWorksheet'
      );
    }
  }, [document.id]);

  // Closing the worksheet closes its step documents before the React root is asynchronously unmounted, so a
  // mounted frame can re-render after its document has left the store.
  if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS && DocumentStore.getDocument(document.id) == null) {
    return null;
  }

  return (
    <ResourcePresenceManager fileId={document.id}>
      <div className={css(styles.editorContainer)}>
        <WorksheetStepHeader
          name={stepName}
          status={stepTaskData.status}
          outStatuses={stepTaskData.outStatuses}
          lastEditedUser={stepTaskData.lastEditedUser}
          timestamp={stepTaskData.timestamp}
          taskIds={stepTaskData.taskIds}
          locked={isSelectedStepActionsLocked}
          enableStepTransitions={enableStepTransitions}
          documentId={document.id}
          isWorksheetArchived={isWorksheetArchived}
        />
        <div
          className={css(styles.documentWrapper)}
          {...getTestProps({component: 'WorksheetStepFrame', element: 'editor'})}
        >
          <DocumentEditor
            id={document.id}
            locked={isSelectedStepDocumentLocked}
            renderBanner={() =>
              isExecutionEnabled && (isSelectedStepDocumentLocked || !isLatest) ? (
                <BannerMessage {...bannerProps} />
              ) : null
            }
            onChange={onDocumentChange}
            tableNamespace={tableNamespace}
            relatedDocumentIds={relatedDocumentIds}
            disablePerformanceIndicator={BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS}
            withDisplayOptions={true}
            // Step documents can hold thousands of table cells; rendering only what is on screen keeps
            // step switching responsive.
            isVirtualizationEnabled={true}
          />
        </div>
      </div>
    </ResourcePresenceManager>
  );
}

export default React.memo(WorksheetStepFrame);

const styles = StyleSheet.create({
  editorContainer: {
    flex: 1,
    overflowY: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  documentWrapper: {
    flex: 1,
    overflowY: 'hidden',
  },
});
